import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Preloader from './components/Preloader';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import TrustBar from './components/TrustBar';
import WhatWeDo from './components/WhatWeDo';
import WhoIsItFor from './components/WhoIsItFor';
import ProblemSolution from './components/ProblemSolution';
import HowItWorks from './components/HowItWorks';
import PlatformEcosystem from './components/PlatformEcosystem';
import CareerJourney from './components/CareerJourney';
import ReportPreview from './components/ReportPreview';
import Plans from './components/Plans';
import DashboardPreview from './components/DashboardPreview';
import WhyParentsTrust from './components/WhyParentsTrust';
import OurPartners from './components/OurPartners';
import OurTeam from './components/OurTeam';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import LiveAssessmentModal from './components/LiveAssessmentModal';
import MindQPlatformViews from './components/MindQPlatformViews';
import AuthView from './components/AuthView';
import ResetPasswordPage from './components/ResetPasswordPage';
import UserDashboard from './components/UserDashboard';
import CheckoutPage from './components/CheckoutPage';
import AssessmentRedirectModal from './components/AssessmentRedirectModal';
import ReportDashboardPage from './components/ReportDashboardPage';
import ExpertOnboardingPage from './components/ExpertOnboardingPage';
import AdminMembersPage from './components/AdminMembersPage';
import AdminLoginPage from './components/AdminLoginPage';
import { experts } from './data/platformData';
import { authService } from './services/authService';
import { paymentService } from './services/paymentService';
import { ASSESSMENT_STATUSES, assessmentService } from './services/assessmentService';

const isAdminOnlyBuild = process.env.REACT_APP_PORTAL_MODE === 'admin';

const ProtectedDashboard = ({ user, onLogout, onChoosePlan, onCompletePayment, onAssessmentSetup, onOpenReports }) => {
  if (isAdminOnlyBuild) return <Navigate to="/admin/login" replace />;
  if (!user) return <Navigate to="/auth" replace state={{ from: '/dashboard' }} />;
  if (user.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  return <UserDashboard user={user} onLogout={onLogout} onChoosePlan={onChoosePlan} onCompletePayment={onCompletePayment} onAssessmentSetup={onAssessmentSetup} onOpenReports={onOpenReports} />;
};

const ProtectedAdminDashboard = ({ user, onLogout }) => {
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <AdminMembersPage user={user} onLogout={onLogout} />;
};

const ProtectedCheckout = ({ user, onBackToPlans, onPaymentComplete }) => {
  if (isAdminOnlyBuild) return <Navigate to="/admin/login" replace />;
  if (!user) return <Navigate to="/auth" replace state={{ from: '/checkout' }} />;
  return <CheckoutPage user={user} onBackToPlans={onBackToPlans} onPaymentComplete={onPaymentComplete} />;
};

const ProtectedReports = ({ user, onBackToDashboard, onUserUpdate }) => {
  if (isAdminOnlyBuild) return <Navigate to="/admin/login" replace />;
  if (!user) return <Navigate to="/auth" replace state={{ from: '/reports' }} />;
  return <ReportDashboardPage user={user} onBackToDashboard={onBackToDashboard} onUserUpdate={onUserUpdate} />;
};

function AppContent() {
  const [preloaderDone, setPreloaderDone] = useState(false);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [platformView, setPlatformView] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(experts[0]);
  const [user, setUser] = useState(() => authService.getCurrentUser());
  const [assessmentRedirect, setAssessmentRedirect] = useState({
    open: false,
    loading: false,
    session: null,
    redirectInfo: null,
    error: '',
    verificationRequired: false,
    verificationMessage: '',
    devCode: '',
  });
  const [pendingSection, setPendingSection] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setUser(authService.getCurrentUser());
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/' || !pendingSection) return;
    const timer = window.setTimeout(() => {
      document.querySelector(pendingSection)?.scrollIntoView({ behavior: 'smooth' });
      setPendingSection(null);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [location.pathname, pendingSection]);

  const openAuth = () => navigate('/auth');
  const openDashboard = () => navigate('/dashboard');
  const openExpertJoin = (role = 'counselor') => navigate(`/join/${role}`);
  const navigateToSection = (href) => {
    if (!href || href === '#') return;
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    if (location.pathname !== '/') {
      setPendingSection(href);
      navigate('/');
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };
  const openPlans = () => navigateToSection('#plans');

  const handleSelectPlan = (plan) => {
    if (!user) {
      navigate('/auth', { state: { from: '/', reason: 'plan-selection' } });
      return;
    }
    const selectedPlan = paymentService.saveSelectedPlan(user, plan);
    const nextUser = { ...authService.getCurrentUser(), selectedPlan, planStatus: 'selected' };
    setUser(nextUser);
    navigate('/checkout');
  };

  const openAssessment = async () => {
    if (!user) {
      navigate('/auth', { state: { from: '/dashboard', reason: 'assessment' } });
      return;
    }
    if (user.planStatus !== 'selected') {
      openPlans();
      return;
    }
    if (user.paymentStatus !== 'completed') {
      navigate('/checkout');
      return;
    }
    setAssessmentRedirect({ open: true, loading: true, session: null, redirectInfo: null, error: '', verificationRequired: false, verificationMessage: '', devCode: '' });
    try {
      const verification = await assessmentService.getEmailVerificationStatus();
      if (!verification.verified) {
        const sent = await assessmentService.sendEmailVerificationCode();
        setAssessmentRedirect({
          open: true,
          loading: false,
          session: null,
          redirectInfo: null,
          error: '',
          verificationRequired: true,
          verificationMessage: sent.message || 'Verification code sent.',
          devCode: sent.devCode || '',
        });
        return;
      }
      const session = await assessmentService.createAssessmentSession(user);
      const updatedUser = assessmentService.storeAssessmentSession(user, session);
      const redirectInfo = assessmentService.generateAssessmentRedirectUrl(session);
      setUser(updatedUser || authService.getCurrentUser());
      setAssessmentRedirect({ open: true, loading: false, session, redirectInfo, error: '', verificationRequired: false, verificationMessage: '', devCode: '' });
    } catch (error) {
      setAssessmentRedirect({ open: true, loading: false, session: null, redirectInfo: null, error: error.message, verificationRequired: false, verificationMessage: '', devCode: '' });
    }
  };
  const closeAssessment = () => setIsAssessmentOpen(false);

  const handleAuthenticated = (nextUser) => {
    setUser(nextUser);
    navigate(nextUser.role === 'admin' ? '/admin/dashboard' : '/dashboard');
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    navigate(location.pathname.startsWith('/admin') ? '/admin/login' : '/');
  };

  const handlePaymentComplete = (nextUser) => {
    setUser(nextUser || authService.getCurrentUser());
    navigate('/dashboard');
  };

  const handleUserUpdate = (nextUser) => {
    setUser(nextUser || authService.getCurrentUser());
  };

  const continueToAssessmentPartner = async () => {
    if (!assessmentRedirect.session || !assessmentRedirect.redirectInfo?.url) return;
    try {
      const updatedUser = await assessmentService.updateAssessmentStatus(user, ASSESSMENT_STATUSES.redirectedToPartner);
      setUser(updatedUser || authService.getCurrentUser());
      setAssessmentRedirect((current) => ({ ...current, open: false }));
      window.location.href = assessmentRedirect.redirectInfo.url;
    } catch (error) {
      setAssessmentRedirect((current) => ({ ...current, error: error.message || 'Could not update assessment status.' }));
    }
  };

  const verifyAssessmentEmail = async (code) => {
    setAssessmentRedirect((current) => ({ ...current, loading: true, error: '' }));
    try {
      await assessmentService.verifyEmailCode(code);
      const session = await assessmentService.createAssessmentSession(user);
      const updatedUser = assessmentService.storeAssessmentSession(user, session);
      const redirectInfo = assessmentService.generateAssessmentRedirectUrl(session);
      setUser(updatedUser || authService.getCurrentUser());
      setAssessmentRedirect({ open: true, loading: false, session, redirectInfo, error: '', verificationRequired: false, verificationMessage: '', devCode: '' });
    } catch (error) {
      setAssessmentRedirect((current) => ({ ...current, loading: false, error: error.message || 'Verification failed.' }));
    }
  };

  const openPlatformView = (view, expert = selectedExpert) => {
    setSelectedExpert(expert || experts[0]);
    setPlatformView(view);
  };

  const closePlatformView = () => setPlatformView(null);
  const openBooking = (expert = selectedExpert) => openPlatformView('booking', expert);

  return (
    <div className="app">
      {!preloaderDone && (
        <Preloader onComplete={() => setPreloaderDone(true)} />
      )}
      <div
        style={{
          opacity: preloaderDone ? 1 : 0,
          transition: 'opacity 0.8s ease',
          minHeight: '100vh',
        }}
      >
        {!isAdminOnlyBuild && !location.pathname.startsWith('/admin') && (
          <Navbar
            onStartAssessment={openAssessment}
            onOpenCareerLibrary={() => openPlatformView('careers')}
            onOpenAssessment={openAssessment}
            onOpenParentDashboard={openDashboard}
            onOpenAuth={openAuth}
            onNavigateSection={navigateToSection}
            user={user}
          />
        )}
        <Routes>
          <Route
            path="/"
            element={isAdminOnlyBuild ? <Navigate to="/admin/login" replace /> : (
              <>
                <main>
                  <Hero onStartAssessment={openAssessment} onOpenAssessment={openAssessment} onOpenPlans={openPlans} />
                  <TrustBar />
                  <WhatWeDo />
                  <WhoIsItFor onOpenAssessment={openAssessment} onOpenJoin={() => openExpertJoin('counselor')} />
                  <ProblemSolution />
                  <HowItWorks onOpenAssessment={openAssessment} />
                  <PlatformEcosystem />
                  <CareerJourney onOpenAssessment={openAssessment} />
                  <ReportPreview onOpenReport={() => openPlatformView('assessment')} />
                  <Plans onOpenAssessment={openAssessment} onSelectPlan={handleSelectPlan} />
                  <DashboardPreview />
                  <WhyParentsTrust />
                  <OurPartners />
                  <FAQ />
                  <FinalCTA
                    onStartAssessment={openAssessment}
                    onOpenAssessment={openAssessment}
                    onOpenPlans={openPlans}
                    onOpenJoin={() => openExpertJoin('counselor')}
                  />
                </main>
                <Footer
                  onOpenJoin={() => openExpertJoin('counselor')}
                  onOpenCareerLibrary={() => openPlatformView('careers')}
                  onOpenParentDashboard={openDashboard}
                  onNavigateSection={navigateToSection}
                />
              </>
            )}
          />
          <Route
            path="/team"
            element={isAdminOnlyBuild ? <Navigate to="/admin/login" replace /> : (
              <>
                <main style={{ minHeight: '100vh', background: 'rgba(255,255,255,1)' }}>
                  <OurTeam />
                </main>
                <Footer
                  onOpenJoin={() => openExpertJoin('counselor')}
                  onOpenCareerLibrary={() => openPlatformView('careers')}
                  onOpenParentDashboard={openDashboard}
                  onNavigateSection={navigateToSection}
                />
              </>
            )}
          />
          <Route path="/auth" element={isAdminOnlyBuild ? <Navigate to="/admin/login" replace /> : <AuthView onAuthenticated={handleAuthenticated} />} />
          <Route path="/admin/login" element={<AdminLoginPage onAuthenticated={handleAuthenticated} />} />
          <Route path="/admin/dashboard" element={<ProtectedAdminDashboard user={user} onLogout={handleLogout} />} />
          <Route path="/reset-password" element={isAdminOnlyBuild ? <Navigate to="/admin/login" replace /> : <ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={(
              <ProtectedDashboard
                user={user}
                onLogout={handleLogout}
                onChoosePlan={openPlans}
                onCompletePayment={() => navigate('/checkout')}
                onAssessmentSetup={openAssessment}
                onOpenReports={() => navigate('/reports')}
              />
            )}
          />
          <Route path="/checkout" element={<ProtectedCheckout user={user} onBackToPlans={openPlans} onPaymentComplete={handlePaymentComplete} />} />
          <Route path="/reports" element={<ProtectedReports user={user} onBackToDashboard={openDashboard} onUserUpdate={handleUserUpdate} />} />
          <Route path="/join/:role" element={isAdminOnlyBuild ? <Navigate to="/admin/login" replace /> : <ExpertOnboardingPage />} />
          <Route path="*" element={<Navigate to={isAdminOnlyBuild ? '/admin/login' : '/'} replace />} />
        </Routes>
        <MindQPlatformViews
          view={platformView}
          selectedExpert={selectedExpert}
          onClose={closePlatformView}
          onOpenBooking={openBooking}
        />
      </div>
      <LiveAssessmentModal isOpen={isAssessmentOpen} onClose={closeAssessment} />
      {assessmentRedirect.open && (
        <AssessmentRedirectModal
          session={assessmentRedirect.session}
          redirectInfo={assessmentRedirect.redirectInfo}
          loading={assessmentRedirect.loading}
          error={assessmentRedirect.error}
          verificationRequired={assessmentRedirect.verificationRequired}
          verificationMessage={assessmentRedirect.verificationMessage}
          devCode={assessmentRedirect.devCode}
          onVerifyEmail={verifyAssessmentEmail}
          onClose={() => setAssessmentRedirect({ open: false, loading: false, session: null, redirectInfo: null, error: '', verificationRequired: false, verificationMessage: '', devCode: '' })}
          onContinue={continueToAssessmentPartner}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
